<script setup lang="ts">
import {
  getCategoryBreadcrumbs,
  getProductName,
} from "@shopware-pwa/helpers-next";

const props = defineProps<{
  navigationId: string;
}>();

const { search } = useProductSearch();

const { buildDynamicBreadcrumbs, pushBreadcrumb } = useBreadcrumbs();
const { apiClient } = useShopwareContext();

const { data, error } = await useAsyncData(
  `cmsProduct${props.navigationId}`,
  async () => {
    const responses = await Promise.allSettled([
      search(props.navigationId, {
        withCmsAssociations: true,
        criteria: {
          associations: {
            manufacturer: {
              associations: {
                media: {},
              },
            },
            crossSellings: {
              products: {},
            },
            properties: {
              associations: {
                group: {},
                media: {},
              },
            },
            options: {},
            seoUrls: {},
          },
        },
      }),
      apiClient.invoke("readBreadcrumb get /breadcrumb/{id}", {
        pathParams: {
          id: props.navigationId,
        },
      }),
    ]);

    for (const response of responses) {
      if (response.status === "rejected") {
        console.error("[FrontendDetailPage.vue]", response.reason.message);
      }
    }

    return {
      productResponse:
        responses[0].status === "fulfilled" ? responses[0].value : null,
      breadcrumbs:
        responses[1].status === "fulfilled" ? responses[1].value : null,
    };
  }
);

const productResponse = ref(data.value?.productResponse);
if (data.value?.breadcrumbs?.data) {
  buildDynamicBreadcrumbs(data.value.breadcrumbs.data);
}

if (!productResponse.value) {
  console.error("[FrontendDetailPage.vue]", error.value?.message);
  throw error.value;
}

useProductJsonLD(productResponse.value.product);

pushBreadcrumb({
  name: getProductName({ product: productResponse.value.product }) ?? "",
  path: "",
});

const { product } = useProduct(
  productResponse.value.product,
  productResponse.value.configurator
);

const runtimeConfig = useRuntimeConfig();

useCmsHead(product, { mainShopTitle: runtimeConfig.public.general.shopTitle });

provide("product-detail", true);

const route = useRoute();
const { getAbsoluteUrl } = useAbsoluteUrl();
useHead({
  link: [
    {
      rel: "canonical",
      href: getAbsoluteUrl(route.path),
    },
  ],
});
</script>

<template>
  <div class="detail-page">
    <LayoutBreadcrumbs />
    <div v-if="product?.cmsPage">
      <CmsPage :content="product.cmsPage" />
    </div>
  </div>
</template>
